import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api'

interface AccountStatus {
  plan: string;
  readonly: boolean;
  daysUntilDeletion?: number;
  loading: boolean;
  error?: string;
}

export default function useAccountStatus(): AccountStatus {
  const { currentUser, isAuthReady } = useAuth();
  const [status, setStatus] = useState<AccountStatus>({
    plan: 'free',
    readonly: false,
    loading: true,
  });

  useEffect(() => {
    const fetchAccountStatus = async () => {
      if (!currentUser || !isAuthReady) {
        setStatus(s => ({ ...s, loading: false }));
        return;
      }

      try {
        // Get Firebase ID token for authentication
        const token = await currentUser.getIdToken(true);

        // If no token, don't call authenticated endpoints
        if (!token) {
          setStatus({ plan: 'free', readonly: false, loading: false, error: 'Authentication required' })
          return
        }
        
        // Try to get account status from billing endpoint first
        const response = await fetch(`${API_BASE_URL}/api/v1/billing/status`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setStatus({
            plan: data.plan ?? 'free',
            readonly: data.readonly ?? false,
            daysUntilDeletion: data.days_remaining,
            loading: false,
          });
        } else if (response.status === 401) {
          // Authentication error - user might need to re-authenticate
          console.warn('Authentication failed for account status, user may need to re-login');
          setStatus({
            plan: 'free',
            readonly: false,
            loading: false,
            error: 'Authentication required'
          });
        } else {
          // Fallback to basic user info if billing endpoint fails
          // Only attempt fallback if we still have a token
          const userResponse = token ? await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }) : null;

          if (userResponse && userResponse.ok) {
            const userData = await userResponse.json();
            setStatus({
              plan: 'free', // Default to free for new users
              readonly: false,
              loading: false,
            });
          } else {
            throw new Error('Failed to fetch account status');
          }
        }
      } catch (err) {
        console.error('Error fetching account status:', err);
        setStatus(s => ({ 
          ...s, 
          loading: false, 
          error: err instanceof Error ? err.message : 'Failed to fetch account status'
        }));
      }
    };

    fetchAccountStatus();
  }, [currentUser]);

  return status;
}
